"use client";

import { c as _c } from "react/compiler-runtime";
import { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import type { ApiWaveCreditNft } from "@/generated/models/ApiWaveCreditNft";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import CommonBorderedRadioButton from "@/components/utils/radio/CommonBorderedRadioButton";
import CreateWaveVotingRep from "./CreateWaveVotingRep";
import CreateWaveVotingThreshold from "./CreateWaveVotingThreshold";
import CreateWaveVotingThresholdTime from "./CreateWaveVotingThresholdTime";
import MemeCardSetPicker from "./MemeCardSetPicker";
import MaxVotesPerIdentityInput from "./MaxVotesPerIdentityInput";
import NegativeVotingToggle from "./NegativeVotingToggle";
import TimeWeightedVoting from "./TimeWeightedVoting";
import type { TimeWeightedVotingConfig } from "./types";
const VOTING_TYPES_ORDER: Record<ApiWaveCreditType, number | undefined> = {
  [ApiWaveCreditType.TdhPlusXtdh]: 0,
  [ApiWaveCreditType.Tdh]: 1,
  [ApiWaveCreditType.Rep]: 2,
  [ApiWaveCreditType.CardSetTdh]: 3,
  [ApiWaveCreditType.Xtdh]: undefined
};
const TIME_WEIGHTED_DURATION_ERROR = "This interval is longer than the wave duration. Choose a shorter interval, extend the wave end date, or clear the end date.";
const APPROVAL_THRESHOLD_TIME_INVALID_ERROR = "Enter a whole number greater than 0, or leave blank for immediate approval.";
const APPROVAL_THRESHOLD_TIME_DURATION_ERROR = "This time is longer than the wave duration. Choose a shorter time, extend the wave end date, or clear the end date.";
const VOTING_SETTINGS_GRID_CLASSES = "tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-3 tw-border-t tw-border-iron-700 tw-pt-6";
const VOTING_OPTIONS_GRID_CLASSES = "tw-mt-3 tw-grid tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-4 [&>div]:tw-gap-x-2 [&>div]:tw-px-3 [&>div]:tw-py-3";
const getCreateWaveVotingLabel = (votingType: ApiWaveCreditType): string => {
  if (votingType === ApiWaveCreditType.CardSetTdh) {
    return "Memes TDH";
  }
  return WAVE_VOTING_LABELS[votingType];
};
const getApprovalThresholdTimeErrorMessage = (errors: CREATE_WAVE_VALIDATION_ERROR[]): string | undefined => {
  if (errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_TIME_EXCEEDS_WAVE_DURATION)) {
    return APPROVAL_THRESHOLD_TIME_DURATION_ERROR;
  }
  if (errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_TIME_INVALID)) {
    return APPROVAL_THRESHOLD_TIME_INVALID_ERROR;
  }
  return undefined;
};
export default function CreateWaveVoting(t0) {
  const $ = _c(56);
  const {
    waveType,
    selectedType,
    category,
    profileId,
    creditNfts,
    memeCount,
    isMemeCountLoading,
    isMemeCountError,
    allowNegativeVotes,
    maxVotesPerIdentityPerDrop,
    approvalThreshold,
    approvalThresholdTimeMs,
    errors,
    onTypeChange,
    setCategory,
    setProfileId,
    setCreditNfts,
    onAllowNegativeVotesChange,
    setMaxVotesPerIdentityPerDrop,
    setApprovalThreshold,
    setApprovalThresholdTimeMs,
    timeWeighted,
    onTimeWeightedChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      [ApiWaveType.Chat]: "How Drops are Rated",
      [ApiWaveType.Rank]: "How Drops are Voted",
      [ApiWaveType.Approve]: "How Drops are Voted"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const TITLES = t1;
  if (selectedType === null) {
    return null;
  }
  const timeWeightedErrorMessage = errors.includes(CREATE_WAVE_VALIDATION_ERROR.TIME_WEIGHTED_VOTING_INTERVAL_EXCEEDS_WAVE_DURATION) ? TIME_WEIGHTED_DURATION_ERROR : undefined;
  let t2;
  if ($[1] !== errors) {
    t2 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_REQUIRED);
    $[1] = errors;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const approvalThresholdError = t2;
  let t3;
  if ($[3] !== errors) {
    t3 = getApprovalThresholdTimeErrorMessage(errors);
    $[3] = errors;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const approvalThresholdTimeErrorMessage = t3;
  const showVotingSettings = waveType !== ApiWaveType.Chat;
  const t4 = TITLES[waveType];
  let t5;
  if ($[5] !== t4) {
    t5 = <p className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-50 sm:tw-text-xl">{t4}</p>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== onTypeChange || $[8] !== selectedType) {
    t6 = (Object.keys(VOTING_TYPES_ORDER) as ApiWaveCreditType[]).filter(_temp).map(votingType_0 => <CommonBorderedRadioButton key={votingType_0} type={votingType_0} selected={selectedType} disabled={false} variant="subtle" onChange={onTypeChange}><span className={`tw-flex tw-min-h-4 tw-items-center tw-text-sm tw-font-semibold ${selectedType === votingType_0 ? "tw-text-white" : "tw-text-iron-300 group-hover:tw-text-white"}`}>{getCreateWaveVotingLabel(votingType_0)}</span></CommonBorderedRadioButton>);
    $[7] = onTypeChange;
    $[8] = selectedType;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== category || $[11] !== errors || $[12] !== profileId || $[13] !== selectedType || $[14] !== setCategory || $[15] !== setProfileId) {
    t7 = selectedType === ApiWaveCreditType.Rep && <div className="tw-col-span-full"><CreateWaveVotingRep category={category} profileId={profileId} errors={errors} setCategory={setCategory} setProfileId={setProfileId} /></div>;
    $[10] = category;
    $[11] = errors;
    $[12] = profileId;
    $[13] = selectedType;
    $[14] = setCategory;
    $[15] = setProfileId;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== creditNfts || $[18] !== errors || $[19] !== isMemeCountError || $[20] !== isMemeCountLoading || $[21] !== memeCount || $[22] !== selectedType || $[23] !== setCreditNfts) {
    t8 = selectedType === ApiWaveCreditType.CardSetTdh && <MemeCardSetPicker creditNfts={creditNfts} memeCount={memeCount} isMemeCountLoading={isMemeCountLoading} isMemeCountError={isMemeCountError} errors={errors} onCreditNftsChange={setCreditNfts} />;
    $[17] = creditNfts;
    $[18] = errors;
    $[19] = isMemeCountError;
    $[20] = isMemeCountLoading;
    $[21] = memeCount;
    $[22] = selectedType;
    $[23] = setCreditNfts;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t9 = <div className={VOTING_OPTIONS_GRID_CLASSES}>{t6}{t7}{t8}</div>;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== approvalThreshold || $[30] !== approvalThresholdError || $[31] !== approvalThresholdTimeErrorMessage || $[32] !== approvalThresholdTimeMs || $[33] !== errors || $[34] !== maxVotesPerIdentityPerDrop || $[35] !== setApprovalThreshold || $[36] !== setApprovalThresholdTimeMs || $[37] !== setMaxVotesPerIdentityPerDrop || $[38] !== showVotingSettings || $[39] !== waveType) {
    t10 = showVotingSettings && <div data-testid="create-wave-voting-settings-grid" className={VOTING_SETTINGS_GRID_CLASSES}><MaxVotesPerIdentityInput value={maxVotesPerIdentityPerDrop} errors={errors} onChange={setMaxVotesPerIdentityPerDrop} />{waveType === ApiWaveType.Approve && <><CreateWaveVotingThreshold threshold={approvalThreshold} error={approvalThresholdError} setThreshold={setApprovalThreshold} /><CreateWaveVotingThresholdTime thresholdTimeMs={approvalThresholdTimeMs} errorMessage={approvalThresholdTimeErrorMessage} setThresholdTimeMs={setApprovalThresholdTimeMs} /></>}</div>;
    $[29] = approvalThreshold;
    $[30] = approvalThresholdError;
    $[31] = approvalThresholdTimeErrorMessage;
    $[32] = approvalThresholdTimeMs;
    $[33] = errors;
    $[34] = maxVotesPerIdentityPerDrop;
    $[35] = setApprovalThreshold;
    $[36] = setApprovalThresholdTimeMs;
    $[37] = setMaxVotesPerIdentityPerDrop;
    $[38] = showVotingSettings;
    $[39] = waveType;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== allowNegativeVotes || $[42] !== onAllowNegativeVotesChange || $[43] !== waveType) {
    t11 = waveType !== ApiWaveType.Chat && <NegativeVotingToggle allowNegativeVotes={allowNegativeVotes} onChange={onAllowNegativeVotesChange} isDisabled={false} />;
    $[41] = allowNegativeVotes;
    $[42] = onAllowNegativeVotesChange;
    $[43] = waveType;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== onTimeWeightedChange || $[46] !== timeWeighted || $[47] !== timeWeightedErrorMessage || $[48] !== waveType) {
    t12 = waveType !== ApiWaveType.Chat && <TimeWeightedVoting config={timeWeighted} errorMessage={timeWeightedErrorMessage} onChange={onTimeWeightedChange} />;
    $[45] = onTimeWeightedChange;
    $[46] = timeWeighted;
    $[47] = timeWeightedErrorMessage;
    $[48] = waveType;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] !== t10 || $[51] !== t11 || $[52] !== t12 || $[53] !== t5 || $[54] !== t9) {
    t13 = <div>{t5}{t9}{t10}{t11}{t12}</div>;
    $[50] = t10;
    $[51] = t11;
    $[52] = t12;
    $[53] = t5;
    $[54] = t9;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  return t13;
}
function _temp(votingType) {
  return VOTING_TYPES_ORDER[votingType] !== undefined;
}